<script setup lang="ts">
import { ref, onMounted, computed, onBeforeUnmount, defineProps } from "vue";

const props = defineProps<{
  timeFormat: string;
  showSeconds: boolean;
  fontFamily: string;
  fontSize: number;
  fontColor: string;
  fontWeight: string;
}>();

const currentTime = ref(new Date());
const clockTimer = ref<number | null>(null);

const formattedTime = computed(() => {
  const hours = currentTime.value.getHours();
  const minutes = currentTime.value.getMinutes();
  const seconds = currentTime.value.getSeconds();

  // time format
  let formattedHours = hours;
  let hourString = "";

  if (props.timeFormat === "12h") {
    formattedHours = hours % 12;
    formattedHours = formattedHours || 12;
    hourString = formattedHours.toString();
  } else {
    // Add leading zero for hours in 24h format
    hourString = formattedHours.toString().padStart(2, "0");
  }

  const formattedMinutes = minutes.toString().padStart(2, "0");

  let timeString = `${hourString}:${formattedMinutes}`;
  if (props.showSeconds) {
    const formattedSeconds = seconds.toString().padStart(2, "0");
    timeString += `:${formattedSeconds}`;
  }

  if (props.timeFormat === "12h") {
    timeString += hours >= 12 ? " PM" : " AM";
  }

  return timeString;
});

onMounted(() => {
  updateTime();
  //update time every second
  const interval = props.showSeconds ? 1000 : 60000;
  clockTimer.value = window.setInterval(updateTime, interval);
});

onBeforeUnmount(() => {
  if (clockTimer.value) {
    window.clearInterval(clockTimer.value);
  }
});

function updateTime() {
  currentTime.value = new Date();
}
</script>

<script lang="ts">
export default {
  name: "ClockWidget",
};
</script>

<template>
  <div
    class="clock-widget"
    :style="{
      fontFamily: fontFamily,
      fontSize: `${fontSize}px`,
      color: fontColor,
      fontWeight: fontWeight === 'bold' ? 'bold' : 'normal',
      fontStyle: fontWeight === 'italic' ? 'italic' : 'normal',
      textDecoration: fontWeight === 'underline' ? 'underline' : 'none',
    }"
  >
    {{ formattedTime }}
  </div>
</template>

<style scoped>
.clock-widget {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
  padding: 10px;
}
</style>
